import ClockIcon from '@/asset/icons/clock';
import { ClassEnum, SETTING_TYPE, timeFormatEnum } from '@/constants/enum';
import { getColor, time24h } from '@/helpers';
import { previewOptionSelector } from '@/redux/slice/preview.slice';
import { handleSavePreviewDateTimeSettings, previewDateTimeSelector } from '@/redux/slice/previewDateTime.slice';
import { widgetColorSelector, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { widgetTextDetailSelector } from '@/redux/slice/widgetText.slice';
import { convertTime24h, time24hTo12h } from '@/utils';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from '../../components/Dropdown';
import useRequiredMessage from '@/hooks/useRequiredMessage';

const TimePicker = () => {
  const widgetTextDetail = useSelector(widgetTextDetailSelector);

  const widgetShippingTexts = useMemo(
    () => widgetTextDetail.texts?.find((item) => item.type === SETTING_TYPE.SHIPPING),
    [widgetTextDetail.texts],
  );

  const widgetDeliveryTexts = useMemo(
    () => widgetTextDetail.texts?.find((item) => item.type === SETTING_TYPE.DELIVERY),
    [widgetTextDetail.texts],
  );

  const widgetPickupTexts = useMemo(
    () => widgetTextDetail.texts?.find((item) => item.type === SETTING_TYPE.PICKUP),
    [widgetTextDetail.texts],
  );

  const widgetSettings = useSelector(widgetSettingSelector);
  const previewOptions = useSelector(previewOptionSelector);
  const previewDateTimes = useSelector(previewDateTimeSelector);
  const widgetColors = useSelector(widgetColorSelector);
  const dispatch = useDispatch();

  const { isShowRequiredMessage } = useRequiredMessage();

  const timeTitle = useMemo(() => {
    switch (previewOptions.methods.find((item) => item.selected)?.key) {
      case SETTING_TYPE.DELIVERY:
        return widgetDeliveryTexts?.labelAndButtons?.timeTitle;
      case SETTING_TYPE.PICKUP:
        return widgetPickupTexts?.labelAndButtons?.timeTitle;
      case SETTING_TYPE.SHIPPING:
        return widgetShippingTexts?.labelAndButtons?.timeTitle;
      default:
        return '';
    }
  }, [
    previewOptions.methods,
    widgetDeliveryTexts?.labelAndButtons?.timeTitle,
    widgetPickupTexts?.labelAndButtons?.timeTitle,
    widgetShippingTexts?.labelAndButtons?.timeTitle,
  ]);

  const timeInputTitle = useMemo(() => {
    switch (previewOptions.methods.find((item) => item.selected)?.key) {
      case SETTING_TYPE.DELIVERY:
        return widgetDeliveryTexts?.labelAndButtons?.captionTimeTitle;
      case SETTING_TYPE.PICKUP:
        return widgetPickupTexts?.labelAndButtons?.captionTimeTitle;
      case SETTING_TYPE.SHIPPING:
        return widgetShippingTexts?.labelAndButtons?.captionTimeTitle;
      default:
        return '';
    }
  }, [
    previewOptions.methods,
    widgetDeliveryTexts?.labelAndButtons?.captionTimeTitle,
    widgetPickupTexts?.labelAndButtons?.captionTimeTitle,
    widgetShippingTexts?.labelAndButtons?.captionTimeTitle,
  ]);

  const timeOptions = useMemo(() => {
    return time24h.map((item) => {
      if (widgetSettings.customizations.timeFormat === timeFormatEnum.TWENTY_FOUR_HOURS_FORMAT) {
        return {
          label: convertTime24h(item.value),
          value: item.value,
        };
      } else
        return {
          label: time24hTo12h(item.value),
          value: item.value,
        };
    });
  }, [widgetSettings.customizations.timeFormat]);

  return (
    <div className={ClassEnum.TimePickerContainer}>
      {timeTitle && (
        <label
          className={ClassEnum.TimePickerLabel}
          id="time-picker-label"
          style={{ color: getColor(widgetSettings.colorScheme, widgetColors, 'header', 'timeTitleColor') }}
        >
          {timeTitle}
        </label>
      )}

      <Dropdown
        options={timeOptions}
        value={previewDateTimes.previewDateTime.selectedTime}
        onChange={(value) => dispatch(handleSavePreviewDateTimeSettings({ selectedTime: value }))}
        borderRadius={widgetSettings.sizes.borderRadius}
        color={getColor(widgetSettings.colorScheme, widgetColors, 'header', 'inputTextTimeColor')}
        backgroundColor={getColor(widgetSettings.colorScheme, widgetColors, 'header', 'inputBackground')}
        labelPrefix={<ClockIcon id="time-select-icon" width={20} height={20} />}
        placeHolder={timeInputTitle}
        isDisabled={!previewDateTimes.previewDateTime.selectedDate}
        styles={{
          button: {
            border:
              isShowRequiredMessage && !previewDateTimes.previewDateTime.selectedTime
                ? `2px solid ${getColor(widgetSettings.colorScheme, widgetColors, 'body', 'errorColor')}`
                : undefined,
            // animation: isShowRequiredMessage ? 'border-error-blink 2s infinite' : 'none',
          },
        }}
        isClearable
      />
    </div>
  );
};

export default TimePicker;
